/**
 * Wie eine Reise heißt, wenn niemand ihr einen Namen gegeben hat.
 *
 * In der Praxis bleiben Reisen namenlos: Man legt sie an, plant los und tippt
 * nie einen Namen ein. In der Liste standen dann mehrere Einträge „Ohne Namen"
 * untereinander — obwohl Start und Ziel längst feststanden und die Reise damit
 * unverwechselbar beschrieben war.
 *
 * ## Was der Stationsname hier sein muss
 *
 * Ein **Ortsname**. „Bernau → Cannobio" beschreibt eine Reise, „Bernau →
 * Camping Village del Sole" beschreibt eine Ankunft. Bis August 2026 konnte
 * hier beides stehen: `quartiere.befoerdern()` tauschte den Stationsnamen mit
 * gegen den des Campingplatzes, und wer sein Ziel als letztes „hierhin"
 * bestätigte, hatte danach eine Reise, die nach einem Platz hieß. Seit der
 * Trennung von Ort und Platz bleibt der Stationsname der Ortsname — hier ist
 * deshalb nichts zu unterscheiden und nichts zu kürzen. Die Zeile steht
 * trotzdem da, damit niemand den Platznamen zurückholt.
 *
 * Reine Funktion — keine Uhr, kein Netz, kein DOM.
 */

export interface Reisebezeichnung {
  /** Wie die Reise angeschrieben wird. */
  text: string;
  /** Abgeleitet oder selbst vergeben? Die Oberfläche darf das kennzeichnen. */
  abgeleitet: boolean;
}

/** Kürzt eine Adresse auf den Ortsteil davor: „Gruberberg, Hopfgarten, …" → „Gruberberg". */
function kurz(name: string): string {
  return name.split(',')[0].trim();
}

/**
 * Der Name einer Reise.
 *
 * Vorrang hat immer der selbst vergebene. Fehlt er, entsteht „Start → Ziel";
 * ist beides derselbe Ort, wird daraus „Rundreise ab Start" — „Bernau →
 * Bernau" liest sich wie ein Fehler.
 *
 * Bleibt nur ein einziger Ort übrig, steht der allein da. Und wenn gar nichts
 * bekannt ist, bleibt es bei „Ohne Namen": Eine erfundene Bezeichnung wäre
 * hier schlechter als eine ehrliche Lücke.
 */
export function reisename(t: {
  name?: string | null;
  erste_station?: string | null;
  letzte_station?: string | null;
}): Reisebezeichnung {
  const eigen = t.name?.trim();
  if (eigen) return { text: eigen, abgeleitet: false };

  const von = t.erste_station?.trim() ? kurz(t.erste_station) : null;
  const bis = t.letzte_station?.trim() ? kurz(t.letzte_station) : null;

  if (von && bis && von !== bis) return { text: `${von} → ${bis}`, abgeleitet: true };
  if (von && bis && von === bis) return { text: `Rundreise ab ${von}`, abgeleitet: true };
  if (von || bis) return { text: (von ?? bis) as string, abgeleitet: true };

  return { text: 'Ohne Namen', abgeleitet: false };
}
